import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/config';
import { AI_CONFIG } from '../constants/aiConfig';
import { OLLI_TOOLS } from '../constants/olliTools';
import { OlliToolExecutor } from './olliToolExecutor';
import { buildUserContext } from '../utils/ai/contextBuilder';
import { buildSystemPrompt } from '../utils/ai/systemPrompt';

export { SUGGESTED_QUESTIONS } from '../constants/aiConfig';

const askOlliFunction = httpsCallable(functions, 'askOlli');

const MAX_TOOL_ITERATIONS = 5;
// Cap messaggi inviati alla Cloud Function: deve restare <= MAX_MESSAGES_PER_REQUEST
// server-side (12). Tronchiamo lato client PRIMA dell'invio senza mai spezzare una
// coppia tool_use / tool_result.
const MAX_MESSAGES_TO_CLOUD = 10;

// true se il messaggio è un user che porta blocchi tool_result (quindi NON può
// stare in testa alla conversazione senza l'assistant/tool_use che lo precede).
function isToolResultMessage(msg) {
  return (
    msg &&
    msg.role === 'user' &&
    Array.isArray(msg.content) &&
    msg.content.some((b) => b && b.type === 'tool_result')
  );
}

// Mantiene sempre il primo messaggio (la domanda utente) + la coda più recente,
// scartando eventuali tool_result rimasti orfani in testa alla coda. Così non si
// spezza mai una coppia tool_use/tool_result e l'alternanza user/assistant regge.
function trimMessagesPreservingToolPairs(msgs) {
  if (msgs.length <= MAX_MESSAGES_TO_CLOUD) return msgs;
  const first = msgs[0];
  let tail = msgs.slice(msgs.length - (MAX_MESSAGES_TO_CLOUD - 1));
  while (tail.length && isToolResultMessage(tail[0])) {
    tail = tail.slice(1);
  }
  return [first, ...tail];
}

// Timeout massimo lato client per UNA chiamata cloud. La cloud function ha già
// timeoutSeconds: 60, qui aggiungiamo difesa in profondità: niente attesa infinita,
// nessun retry automatico — l'utente deve rilanciare manualmente.
const CLOUD_TIMEOUT_MS = 60_000;
export const TIMEOUT_ERROR_CODE = 'olli/timeout-client';

function generateQuestionId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `q-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export async function askOlli(question, userData, appStateFunctions) {
  const userContext = buildUserContext(userData);
  const systemPrompt = buildSystemPrompt(userContext);

  const executor = appStateFunctions
    ? new OlliToolExecutor(appStateFunctions)
    : null;

  let messages = [{ role: 'user', content: question }];
  let toolUsed = null;
  let toolResult = null;

  // Un questionId per ogni domanda utente: tutte le iterazioni del tool loop
  // condividono lo stesso id, così nei log si possono raggruppare.
  const questionId = generateQuestionId();

  const baseParams = {
    model: AI_CONFIG.model,
    maxTokens: AI_CONFIG.maxTokens,
    temperature: AI_CONFIG.temperature,
    system: systemPrompt,
    questionId,
  };

  if (executor) {
    baseParams.tools = OLLI_TOOLS;
  }

  let iteration = 1;
  let response = await callCloudFunction({ ...baseParams, messages, iteration });

  // Tool use loop: cap a MAX_TOOL_ITERATIONS per evitare runaway costi.
  while (
    response.stop_reason === 'tool_use' &&
    executor &&
    iteration < MAX_TOOL_ITERATIONS
  ) {
    // Tutti i blocchi tool_use del turno CON id valido (il modello può chiamarne
    // più d'uno). I tool_use senza id vengono scartati anche dall'assistant message
    // qui sotto, così non restano mai orfani (difesa: tool_use_id undefined).
    const toolUseBlocks = response.content.filter((b) => b.type === 'tool_use' && b.id);
    if (toolUseBlocks.length === 0) break;

    // Assistant message ripulito dagli eventuali tool_use privi di id (che non
    // processeremmo e che resterebbero senza tool_result).
    const assistantContent = response.content.filter(
      (b) => b.type !== 'tool_use' || b.id
    );

    // Un tool_result per OGNI tool_use, anche se l'esecuzione fallisce o lancia.
    const toolResultBlocks = [];
    for (const block of toolUseBlocks) {
      let result;
      try {
        result = await executor.execute(block.name, block.input);
      } catch (err) {
        result = { success: false, error: err?.message || 'Errore esecuzione tool.' };
      }
      toolUsed = block.name;
      toolResult = result;
      toolResultBlocks.push({
        type: 'tool_result',
        tool_use_id: block.id,
        content: JSON.stringify(result),
        ...(result && result.success === false ? { is_error: true } : {}),
      });
    }

    messages = trimMessagesPreservingToolPairs([
      ...messages,
      { role: 'assistant', content: assistantContent },
      { role: 'user', content: toolResultBlocks },
    ]);

    iteration += 1;
    response = await callCloudFunction({ ...baseParams, messages, iteration });
  }

  const textBlock = response.content.find((b) => b.type === 'text');
  let answer = textBlock?.text || '';

  if (response.stop_reason === 'tool_use' && iteration >= MAX_TOOL_ITERATIONS) {
    answer =
      answer ||
      'Ho raggiunto il limite di azioni per questo messaggio. Prova a riformulare la richiesta in modo più semplice o suddividila in più step.';
  }

  return { answer, toolUsed, toolResult, remainingQuota: response.remainingQuota };
}

async function callCloudFunction(params) {
  let timeoutId;
  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => {
      const err = new Error('Olli non ha risposto in tempo. Riprova manualmente.');
      err.code = TIMEOUT_ERROR_CODE;
      reject(err);
    }, CLOUD_TIMEOUT_MS);
  });

  try {
    const result = await Promise.race([askOlliFunction(params), timeoutPromise]);
    return result.data;
  } finally {
    clearTimeout(timeoutId);
  }
}
